import {
  graphemeLessonPartSchema,
  type GraphemeLessonPart,
} from "@-/dev.improve.haniverse.lib.level";
import { drillTypeCodec, type DrillType } from "@-/dev.improve.haniverse.lib.drill.entities";
import type { LearnCardFace, LearnCardViewMode } from "@-/dev.improve.haniverse.lib.learn.deck";
import type { LearnActiveDeckCard } from "@-/dev.improve.haniverse.routes.nav.app.learn._context";
import { LEARN_DECK_QUEUE_HEAD_PARAMS } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.navigator";

/** The card the deck route opens on. `levelIndex` is resolved from the catalogue at mount. */
export type LearnDeckStudyMode = "manual" | "voice";

export type LearnDeckEntry =
  | { face: LearnCardFace; kind: "slide"; slideSlug: string; view: LearnCardViewMode }
  | {
      face: "front";
      kind: "quiz";
      quizSlug: string;
      /** In the lesson that teaches it, or due among the reviews. */
      source: "lesson" | "review";
      view: "study";
    }
  | {
      /** The word drill's sentence, by its text (see `LearnCardItem`). */
      contextSentence: string | null;
      drillType: DrillType;
      face: LearnCardFace;
      grapheme: string;
      kind: "review";
      /** Touch or voice review; the deck page starts/stops the voice session to match. */
      mode: LearnDeckStudyMode;
      view: LearnCardViewMode;
    }
  | {
      face: LearnCardFace;
      grapheme: string;
      kind: "lesson";
      /** A pronunciation-level lesson (Level `2-P1`) rather than the character's own lesson. */
      part?: GraphemeLessonPart;
      /**
       * Single-reading 2-P1 slot (tone-number pinyin). Distinguishes 干 gan4 from 干 gan1 when both
       * appear as separate pronunciation lessons.
       */
      pinyin?: string;
      view: LearnCardViewMode;
    };

/**
 * The deck route's search params. One route hosts every card, so the current card is carried in
 * the URL (and kept in sync by `setParams` as the deck advances) rather than in the path.
 */
export type LearnDeckParams = {
  contextSentence?: string;
  face?: string;
  kind?: string;
  mode?: string;
  morpheme?: string;
  part?: string;
  pinyin?: string;
  quizSlug?: string;
  quizSource?: string;
  type?: string;
  view?: string;
};

function first(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

export function learnDeckEntryFromParams(
  params: Record<string, string | string[] | undefined>,
): LearnDeckEntry | null {
  if (first(params.kind) === "slide") {
    const slideSlug = first(params.slideSlug);
    return slideSlug ? { face: "front", kind: "slide", slideSlug, view: "study" } : null;
  }
  if (first(params.kind) === "quiz") {
    const quizSlug = first(params.quizSlug);
    return quizSlug
      ? {
          face: "front",
          kind: "quiz",
          quizSlug,
          source: first(params.quizSource) === "review" ? "review" : "lesson",
          view: "study",
        }
      : null;
  }
  const grapheme = first(params.morpheme);
  if (grapheme === undefined || grapheme === "") {
    return null;
  }
  const face: LearnCardFace = first(params.face) === "back" ? "back" : "front";
  const view: LearnCardViewMode = first(params.view) === "info" ? "info" : "study";
  if (first(params.kind) === "lesson") {
    const pinyin = first(params.pinyin);
    const parsedPart = graphemeLessonPartSchema.safeParse(first(params.part));
    if (parsedPart.success) {
      return {
        face,
        grapheme,
        kind: "lesson",
        part: parsedPart.data,
        ...(pinyin === undefined || pinyin === "" || parsedPart.data !== "pronunciation"
          ? {}
          : { pinyin }),
        view,
      };
    }
    return { face, grapheme, kind: "lesson", view };
  }
  const contextSentence = first(params.contextSentence);
  return {
    contextSentence:
      contextSentence === undefined || contextSentence === "" ? null : contextSentence,
    drillType: drillTypeCodec.decode(first(params.type)),
    face,
    grapheme,
    kind: "review",
    mode: first(params.mode) === "voice" ? "voice" : "manual",
    view,
  };
}

/** The deck route names the head of the study queue instead of a card (the summary's Start). */
export function isLearnDeckQueueHeadParams(params: { kind?: string | undefined }) {
  return params.kind === LEARN_DECK_QUEUE_HEAD_PARAMS.kind;
}

export function learnDeckParamsForEntry(entry: LearnDeckEntry): Record<string, string> {
  if (entry.kind === "slide") {
    return { face: "front", kind: "slide", slideSlug: entry.slideSlug, view: "study" };
  }
  if (entry.kind === "quiz") {
    return {
      face: "front",
      kind: "quiz",
      quizSlug: entry.quizSlug,
      quizSource: entry.source,
      view: "study",
    };
  }
  const shared = {
    face: entry.face,
    kind: entry.kind,
    morpheme: entry.grapheme,
    view: entry.view,
  };
  if (entry.kind === "lesson") {
    return entry.part === undefined
      ? shared
      : {
          ...shared,
          part: entry.part,
          ...(entry.pinyin === undefined ? {} : { pinyin: entry.pinyin }),
        };
  }
  return {
    ...shared,
    ...(entry.contextSentence === null ? {} : { contextSentence: entry.contextSentence }),
    mode: entry.mode,
    type: drillTypeCodec.encode(entry.drillType) ?? "meaning",
  };
}

/** What persistent Learn chrome (toolbar pills, header expansion) needs to know about a card. */
export function learnActiveDeckCardForEntry(entry: LearnDeckEntry): LearnActiveDeckCard | null {
  if (entry.kind === "slide" || entry.kind === "quiz") {
    return null;
  }
  if (entry.kind === "lesson") {
    return entry.part === undefined
      ? { grapheme: entry.grapheme, kind: "lesson" }
      : {
          grapheme: entry.grapheme,
          kind: "lesson",
          part: entry.part,
          ...(entry.pinyin === undefined ? {} : { pinyin: entry.pinyin }),
        };
  }
  return {
    contextSentence: entry.contextSentence,
    drillType: entry.drillType,
    grapheme: entry.grapheme,
    kind: "review",
    mode: entry.mode,
  };
}
